import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container } from '@/components/container';
import { KeenIcon } from '@/components/keenicons';
import { useAuthContext } from '@/auth';
import DataTable from 'datatables.net-react';
import DT from 'datatables.net-dt';
import 'datatables.net-select-dt';
import 'datatables.net-responsive-dt';
import { createDataTableOptions, createDataTableConfig } from '@/utils/dataTableConfig';
import { getAccessToken } from '@/utils/LocalStorage';
import { renderMultiAvatars } from '@/utils/renderMultiAvatars';
import { createRoot } from 'react-dom/client';
import { SelectInput, FilterContainer } from '@/components/TableFilter';
import { RESPONSE_LABELS, STATUS_LABELS, money, statusBadge } from '@/components/matter/demandLetters/letterStatus';

DataTable.use(DT);

const STATUS_OPTIONS = Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label }));
const EXPIRING_OPTIONS = [
  { value: '7', label: 'Expiring within 7 days' },
  { value: '14', label: 'Expiring within 14 days' },
  { value: '30', label: 'Expiring within 30 days' },
];

/**
 * Firm-wide "Demands out" view: every demand letter across matters, with the
 * expiring-soon filter the negotiation dashboards in CASEpeer / Litify have.
 */
const DemandLetters = () => {
  const { logout } = useAuthContext();
  const navigate = useNavigate();
  const tableRef = useRef(null);
  const [showFilter, setShowFilter] = useState(false);
  const [filterData, setFilterData] = useState({ status: null, expiring_within: null });
  const filterRef = useRef(filterData);
  const token = getAccessToken();

  useEffect(() => { filterRef.current = filterData; }, [filterData]);

  const open = (row) => navigate(`/demand-letters/${row.id}`);

  const columns = [
    { title: 'Letter', name: 'title', data: 'title', render: (v) => `<span class="link cursor-pointer">${(v)}</span>` },
    {
      title: 'Matter', name: 'matter', data: null,
      render: (d, t, row) => {
        const client = row.matter?.contact ? [row.matter.contact.first_name, row.matter.contact.last_name].filter(Boolean).join(' ') : '';
        return ([row.matter?.name, client].filter(Boolean).join(' · ') || '-');
      },
    },
    { title: 'Recipient', name: 'recipient_company', data: null, render: (d, t, row) => (row.recipient_company || row.recipient_name || '-') },
    { title: 'Status', name: 'status', data: 'status', render: (v) => statusBadge(v) },
    { title: 'Demand', name: 'demand_amount', data: 'demand_amount', render: (v) => money(v) },
    { title: 'Sent', name: 'sent_at', data: 'sent_at', render: (v) => v || '-' },
    { title: 'Deadline', name: 'deadline_at', data: 'deadline_at', render: (v) => v || '-' },
    {
      title: 'Response', name: 'response_status', data: null,
      render: (d, t, row) => row.response_status ? `${RESPONSE_LABELS[row.response_status] || row.response_status}${row.response_amount ? ` · ${money(row.response_amount)}` : ''}` : '-',
    },
    { title: 'Attorney', name: 'attorney', data: null, render: (d, t, row) => (row.attorney ? `${row.attorney.first_name} ${row.attorney.last_name}` : '-') },
    { title: 'Created By', name: 'created_by', data: 'created_by', render: (d, t, row) => `<div id="letter-created-by-${row.id}"></div>` },
  ];

  const refresh = () => tableRef.current?.dt()?.ajax.reload();

  const ajaxConfig = createDataTableConfig('/demand-letters/data_table_list', filterRef, token, logout);

  const options = createDataTableOptions({
    order: [[6, 'asc']],
    drawCallback: function () {
      const api = this.api();
      api.rows({ page: 'current' }).every(function () {
        const row = this.data();
        const cell = this.node()?.querySelector('td .link');
        if (cell && !cell._bound) {
          cell._bound = true;
          cell.addEventListener('click', () => open(row));
        }
        const createdBy = document.getElementById(`letter-created-by-${row.id}`);
        if (createdBy) {
          if (!createdBy._reactRoot) createdBy._reactRoot = createRoot(createdBy);
          createdBy._reactRoot.render(renderMultiAvatars(row.created_by));
        }
      });
    },
  });

  const handleFilterChange = (key, val) => {
    setFilterData((prev) => {
      const updated = { ...prev, [key]: val };
      filterRef.current = updated;
      return updated;
    });
  };

  const resetFilters = () => {
    const cleared = { status: null, expiring_within: null };
    setFilterData(cleared);
    filterRef.current = cleared;
    refresh();
  };

  return (
    <Container>
      <FilterContainer show={showFilter} onApply={refresh} onReset={resetFilters}>
        <SelectInput label="Status" value={filterData.status} onChange={(v) => handleFilterChange('status', v)} staticOptions={STATUS_OPTIONS} customClass="sm:w-[32%] w-full" />
        <SelectInput label="Deadline" value={filterData.expiring_within} onChange={(v) => handleFilterChange('expiring_within', v)} staticOptions={EXPIRING_OPTIONS} customClass="sm:w-[32%] w-full" />
      </FilterContainer>

      <div className="card min-w-full mb-4">
        <div className="card-header">
          <div>
            <h3 className="card-title">Demand Letters</h3>
            <p className="text-xs text-gray-500 mt-0.5">Every demand across the firm. Filter to what expires soon or has no response.</p>
          </div>
          <div className="flex items-center flex-wrap gap-2">
            <button className="text-xs btn btn-sm btn-primary btn-outline" onClick={() => setShowFilter((p) => !p)}>
              <KeenIcon icon="filter" className="w-fit btn-icon" /> {showFilter ? 'Hide Filters' : 'Filters'}
            </button>
          </div>
        </div>
        <DataTable id="demand-letters-dashboard" ajax={ajaxConfig} columns={columns} options={options} ref={tableRef} />
      </div>
    </Container>
  );
};

export { DemandLetters };
